import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement } from 'react';
import { pick as rPick, forEachObj as rForEachObj } from 'remeda';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { SESSION_STORAGE } from '@/constants/storage';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import { CampaignTypeEnum } from '@/libs/openapi';
import { TOAST_TIMEOUT, tryCatch, PROGRESS_INDICATOR_MIN_DELAY_DURATION, minDelay } from '@/libs/serenity-ui';
import { campaignRequestProgramMutations, campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { ProgramForm, type ProgramFormValues } from '@/routes/_main/_form/programs/-components/campaign-request-program-form.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/programs/create/')({
  component: RouteComponent,
  staticData: {
    form: {
      heading: 'Add program',
      toolbarLayout: 'centered',
      goBackFallbackPath: '/programs',
      slots: {
        actionsComponent: ActionsComponent,
      },
    },
  },
  beforeLoad: async ({ context }) => {
    if (context.user && !context.programsFeatureEnabled) {
      throw redirect({ to: '/campaigns' });
    }

    if (context.user && !context.can.campaignRequestPrograms.view()) {
      throw redirect({ to: '/' });
    }

    const can = {
      [Resource.campaignRequestProgram as const]: rPick(
        campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }),
        [ 'create', 'update' ],
      ),
    };

    if (!can.campaignRequestProgram.create()) {
      throw redirect({ to: '/programs' });
    }

    return {
      can,
      currentUserId: context.user.original.id,
      currentUserName: context.user.meta.fullName ?? '',
    };
  },
  head: () => ({
    meta: [{ title: 'TrendMD | Create program' }],
  }),
});

function ActionsComponent(): ReactElement {
  return <SecondaryActionsIconButton />;
}

function RouteComponent(): ReactElement {
  const navigate = Route.useNavigate();
  const routeContext = Route.useRouteContext();

  const createMutation = useMutation(campaignRequestProgramMutations.create);

  const form = useForm({
    defaultValues: {
      name: '',
      agency: '',
      timezone: timezone.get(),
      startDate: undefined,
      endDate: undefined,
      cost: undefined,
      totalBudget: undefined,
      monthlyBudget: {},
      targeting: '',
      ownerUserId: routeContext.currentUserId,
      ownerSearchInput: routeContext.currentUserName,
    } as ProgramFormValues,
    onSubmit: async ({ value }) => {
      const monthlyBudgetCents = Object.fromEntries(Object.entries(value.monthlyBudget).map(([ k, v ]) => [ k, Math.round(Number(v) * 100) ]));

      const createResponse = await tryCatch(minDelay(createMutation.mutateAsync({
        name: value.name,
        agency: value.agency || undefined,
        startDate: value.startDate!,
        endDate: value.endDate!,
        timezone: value.timezone,
        campaignType: CampaignTypeEnum.CPC,
        cost: Math.round((value.cost ?? 0) * 100),
        totalBudget: Math.round((value.totalBudget ?? 0) * 100),
        monthlyBudget: monthlyBudgetCents,
        targeting: value.targeting || undefined,
        ownerUserId: value.ownerUserId,
      }), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

      if (createResponse.error) {
        routeContext.toastQueue.add(
          {
            message: createResponse.error.message,
            variant: 'error',
          },
          { timeout: TOAST_TIMEOUT },
        );
        return;
      }

      clearDraft();
      routeContext.queryClient.invalidateQueries(campaignRequestProgramQueries.all);

      navigate({ to: '/programs/$programId', params: { programId: createResponse.data.id }});
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<ProgramFormValues>({
    storageKey: SESSION_STORAGE.campaignRequestProgramCreateDraft.key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  return (
    <ProgramForm
      canUpdate={routeContext.can.campaignRequestProgram.create()}
      cancelDraft={cancelDraft}
      form={form}
      hasDraft={hasDraft}
      restoreDraft={restoreDraft}
      submitLabel="Add"
    />
  );
}
